import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppThemeProvider } from './contexts/ThemeContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { ModuleProvider } from './contexts/ModuleContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AppShell } from './components/layout/AppShell';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { ModulePage } from './pages/ModulePage';
import { Settings } from './pages/Settings';
import { ProfilePage } from './pages/ProfilePage';
import { RolesPage } from './pages/RolesPage';
import { SuperAdminDashboard } from './pages/superadmin/SuperAdminDashboard';
import { TenantsPage } from './pages/superadmin/TenantsPage';
import { TenantModuleAccessPage } from './pages/superadmin/TenantModuleAccessPage';
import { PendingApprovalsPage } from './pages/superadmin/PendingApprovalsPage';
import { PlansPage } from './pages/superadmin/PlansPage';
import { TransactionsPage } from './pages/superadmin/TransactionsPage';
import { TransactionDetailPage } from './pages/superadmin/TransactionDetailPage';
import { SubscriptionsPage } from './pages/superadmin/SubscriptionsPage';
import { BillingInvoicesPage } from './pages/superadmin/BillingInvoicesPage';
import { BillingInvoiceDetailPage } from './pages/superadmin/BillingInvoiceDetailPage';
import { CoreModulesPage } from './pages/superadmin/CoreModulesPage';
import { FeatureFlagsPage } from './pages/superadmin/FeatureFlagsPage';
import { FieldTypesPage } from './pages/superadmin/FieldTypesPage';
import { RelationshipRulesPage } from './pages/superadmin/RelationshipRulesPage';
import { MasterDataPage } from './pages/superadmin/MasterDataPage';
import { SuperAdminUsersPage } from './pages/superadmin/SuperAdminUsersPage';
import { SecurityPage } from './pages/superadmin/SecurityPage';
import { IntegrationsPage } from './pages/superadmin/IntegrationsPage';
import { PlatformSettingsPage } from './pages/superadmin/PlatformSettingsPage';
import { SuperAdminReportTemplatesPage } from './pages/superadmin/SuperAdminReportTemplatesPage';
import { PlatformBillingProfilePage } from './pages/superadmin/PlatformBillingProfilePage';
import { TenantReportAccessPage } from './pages/superadmin/TenantReportAccessPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { FleetReportsPage } from './pages/reports/FleetReportsPage';
import { ModuleSettingsPage } from './pages/settings/ModuleSettingsPage';
import { LayoutBuilderPage } from './pages/settings/LayoutBuilderPage';
import { FieldManagementPage } from './pages/settings/FieldManagementPage';
import { TenantIntegrationsPage } from './pages/settings/TenantIntegrationsPage';
import { VehicleDocumentsPage } from './pages/documents/VehicleDocumentsPage';
import { EntityProfilePage } from './pages/documents/EntityProfilePage';
import { ActivityLogPage } from './pages/ActivityLogPage';
import { CircularProgress, Box, Alert, Typography } from '@mui/material';
import { APP_BASE_PATH } from './constants/appPaths';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { BiltyListPage } from './pages/bilty/BiltyListPage';
import { BiltyFormPage } from './pages/bilty/BiltyFormPage';
import { BiltyDetailPage } from './pages/bilty/BiltyDetailPage';
import { BiltyItemDescriptionsPage } from './pages/bilty/BiltyItemDescriptionsPage';
import { FleetReferencePage } from './pages/vehicles/FleetReferencePage';
import { TripsListPage } from './pages/trips/TripsListPage';
import { GPSTrackingDashboard } from './pages/gps/GPSTrackingDashboard';
import { GPSTrackingDetailPage } from './pages/gps/GPSTrackingDetailPage';
import { GPSDevicesPage } from './pages/gps/GPSDevicesPage';
import { GPSAlertsPage } from './pages/gps/GPSAlertsPage';
import { GPSReportsPage } from './pages/gps/GPSReportsPage';
import { InvoiceListPage } from './pages/invoices/InvoiceListPage';
import { InvoiceDetailPage } from './pages/invoices/InvoiceDetailPage';
import { tenantModules } from './config/modules';
import { useModules } from './contexts/ModuleContext';
import { resolvePermissionModule } from './constants/permissions';

const ProtectedRoute: React.FC<{ children: React.ReactNode; superAdminOnly?: boolean }> = ({
  children,
  superAdminOnly,
}) => {
  const { token, loading, authReady, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (!authReady || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!token) return <Navigate to="/login" replace />;
  if (superAdminOnly && !isSuperAdmin) return <Navigate to="/dashboard" replace />;
  if (!superAdminOnly && isSuperAdmin) {
    if (location.pathname === '/settings' || location.pathname.startsWith('/settings/')) {
      return <Navigate to="/super-admin/settings" replace />;
    }
    return <Navigate to="/super-admin" replace />;
  }

  return <AppShell>{children}</AppShell>;
};

const AccessDeniedPanel: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
    <Alert severity="warning" sx={{ maxWidth: 520, mx: 'auto', textAlign: 'left' }}>
      <Typography variant="body2">{message}</Typography>
    </Alert>
  </Box>
);

function resolveAccessiblePath(
  hasPermission: (module: string, action?: string) => boolean,
  modules: { key: string; path: string }[],
): string | null {
  if (hasPermission('dashboard', 'view')) return '/dashboard';
  const fromSidebar = modules.find((m) => hasPermission(resolvePermissionModule(m.key), 'view'));
  if (fromSidebar) return fromSidebar.path;
  if (hasPermission('settings', 'view')) return '/settings';
  return '/dashboard';
}

const PermissionRoute: React.FC<{
  children: React.ReactNode;
  module: string;
  action?: string;
}> = ({ children, module, action = 'view' }) => {
  const { hasPermission } = useAuth();
  const { modules, loading } = useModules();

  if (!hasPermission(module, action)) {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (module === 'dashboard') {
      const fallback = resolveAccessiblePath(hasPermission, modules);
      if (fallback && fallback !== '/dashboard') {
        return <Navigate to={fallback} replace />;
      }
      return (
        <AccessDeniedPanel message="You do not have permission to view the dashboard. Contact your administrator." />
      );
    }

    const fallback = resolveAccessiblePath(hasPermission, modules);
    if (fallback) return <Navigate to={fallback} replace />;
    return <AccessDeniedPanel message="You do not have permission to access this page." />;
  }

  return <>{children}</>;
};

const UsersStaffRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hasPermission } = useAuth();
  const { modules, loading } = useModules();
  if (!hasPermission('roles', 'view') && !hasPermission('staff', 'view') && !hasPermission('teams', 'view')) {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    const fallback = resolveAccessiblePath(hasPermission, modules);
    if (fallback) return <Navigate to={fallback} replace />;
    return <AccessDeniedPanel message="You do not have permission to open Staff & Login." />;
  }
  return <>{children}</>;
};

/** Block route when Super Admin has not assigned the module to this tenant */
const TenantModuleRoute: React.FC<{
  children: React.ReactNode;
  moduleKey: string;
  action?: string;
}> = ({ children, moduleKey, action = 'view' }) => {
  const { hasPermission } = useAuth();
  const { isModuleEnabled, loading, modules } = useModules();
  const permModule = resolvePermissionModule(moduleKey);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!isModuleEnabled(moduleKey) || !hasPermission(String(permModule), action)) {
    const fallback = resolveAccessiblePath(hasPermission, modules);
    if (fallback) return <Navigate to={fallback} replace />;
    return (
      <AccessDeniedPanel
        message={
          !isModuleEnabled(moduleKey)
            ? 'This module is not enabled for your organization.'
            : 'You do not have permission to access this module.'
        }
      />
    );
  }

  return <>{children}</>;
};


function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Super Admin */}
      <Route path="/super-admin" element={<ProtectedRoute superAdminOnly><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/super-admin/analytics" element={<Navigate to="/super-admin/report-templates" replace />} />
      <Route path="/super-admin/tenants" element={<ProtectedRoute superAdminOnly><TenantsPage /></ProtectedRoute>} />
      <Route path="/super-admin/tenant-modules" element={<ProtectedRoute superAdminOnly><TenantModuleAccessPage /></ProtectedRoute>} />
      <Route path="/super-admin/tenant-reports" element={<ProtectedRoute superAdminOnly><TenantReportAccessPage /></ProtectedRoute>} />
      <Route path="/super-admin/report-templates" element={<ProtectedRoute superAdminOnly><SuperAdminReportTemplatesPage /></ProtectedRoute>} />
      <Route path="/super-admin/tenants/pending" element={<ProtectedRoute superAdminOnly><PendingApprovalsPage /></ProtectedRoute>} />
      <Route path="/super-admin/plans" element={<ProtectedRoute superAdminOnly><PlansPage /></ProtectedRoute>} />
      <Route path="/super-admin/subscriptions" element={<ProtectedRoute superAdminOnly><SubscriptionsPage /></ProtectedRoute>} />
      <Route path="/super-admin/invoices" element={<ProtectedRoute superAdminOnly><BillingInvoicesPage /></ProtectedRoute>} />
      <Route path="/super-admin/billing-profile" element={<ProtectedRoute superAdminOnly><PlatformBillingProfilePage /></ProtectedRoute>} />
      <Route path="/super-admin/invoices/:id" element={<ProtectedRoute superAdminOnly><BillingInvoiceDetailPage /></ProtectedRoute>} />
      <Route path="/super-admin/transactions" element={<ProtectedRoute superAdminOnly><TransactionsPage /></ProtectedRoute>} />
      <Route path="/super-admin/transactions/:id" element={<ProtectedRoute superAdminOnly><TransactionDetailPage /></ProtectedRoute>} />
      <Route path="/super-admin/modules" element={<ProtectedRoute superAdminOnly><CoreModulesPage /></ProtectedRoute>} />
      <Route path="/super-admin/feature-flags" element={<ProtectedRoute superAdminOnly><FeatureFlagsPage /></ProtectedRoute>} />
      <Route path="/super-admin/field-types" element={<ProtectedRoute superAdminOnly><FieldTypesPage /></ProtectedRoute>} />
      <Route path="/super-admin/relationships" element={<ProtectedRoute superAdminOnly><RelationshipRulesPage /></ProtectedRoute>} />
      <Route path="/super-admin/master/:category" element={<ProtectedRoute superAdminOnly><MasterDataPage /></ProtectedRoute>} />
      <Route path="/super-admin/users" element={<ProtectedRoute superAdminOnly><SuperAdminUsersPage /></ProtectedRoute>} />
      <Route path="/super-admin/security" element={<ProtectedRoute superAdminOnly><SecurityPage /></ProtectedRoute>} />
      <Route path="/super-admin/integrations" element={<ProtectedRoute superAdminOnly><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/super-admin/settings" element={<ProtectedRoute superAdminOnly><PlatformSettingsPage /></ProtectedRoute>} />

      {/* Tenant Core */}
      <Route path="/activity-log" element={<ProtectedRoute><PermissionRoute module="activity"><ActivityLogPage /></PermissionRoute></ProtectedRoute>} />
      <Route path="/guide" element={<Navigate to="/dashboard" replace />} />
      <Route path="/documentation" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><PermissionRoute module="dashboard"><Dashboard /></PermissionRoute></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><PermissionRoute module="settings"><Settings /></PermissionRoute></ProtectedRoute>} />
      <Route path="/settings/modules" element={<ProtectedRoute><PermissionRoute module="settings"><ModuleSettingsPage /></PermissionRoute></ProtectedRoute>} />
      <Route path="/settings/layout/:moduleKey" element={<ProtectedRoute><PermissionRoute module="layouts"><LayoutBuilderPage /></PermissionRoute></ProtectedRoute>} />
      <Route path="/settings/fields/:moduleKey" element={<ProtectedRoute><PermissionRoute module="crm" action="edit"><FieldManagementPage /></PermissionRoute></ProtectedRoute>} />
      <Route path="/settings/integrations" element={<ProtectedRoute><PermissionRoute module="integrations"><TenantIntegrationsPage /></PermissionRoute></ProtectedRoute>} />
      <Route path="/roles" element={<ProtectedRoute><TenantModuleRoute moduleKey="roles"><UsersStaffRoute><RolesPage /></UsersStaffRoute></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><TenantModuleRoute moduleKey="reports"><ReportsPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/fleet-reports" element={<ProtectedRoute><TenantModuleRoute moduleKey="reports"><FleetReportsPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/vehicles/types" element={<ProtectedRoute><TenantModuleRoute moduleKey="vehicle_types"><FleetReferencePage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/vehicles/makes" element={<ProtectedRoute><TenantModuleRoute moduleKey="vehicle_makes"><FleetReferencePage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/vehicles/models" element={<ProtectedRoute><TenantModuleRoute moduleKey="vehicle_models"><FleetReferencePage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/vehicles/documents" element={<ProtectedRoute><TenantModuleRoute moduleKey="vehicle_documents"><VehicleDocumentsPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/vehicles/:id" element={<ProtectedRoute><TenantModuleRoute moduleKey="vehicles"><EntityProfilePage profileType="vehicles" /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/helpers/:id" element={<ProtectedRoute><TenantModuleRoute moduleKey="helpers"><EntityProfilePage profileType="helpers" /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/drivers/:id" element={<ProtectedRoute><TenantModuleRoute moduleKey="drivers"><EntityProfilePage profileType="drivers" /></TenantModuleRoute></ProtectedRoute>} />

      {/* GPS Tracking */}
      <Route path="/gps/live" element={<ProtectedRoute><TenantModuleRoute moduleKey="gps_tracking"><GPSTrackingDashboard /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/gps/devices" element={<ProtectedRoute><TenantModuleRoute moduleKey="gps_devices"><GPSDevicesPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/gps/alerts" element={<ProtectedRoute><TenantModuleRoute moduleKey="gps_alerts"><GPSAlertsPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/gps/reports" element={<ProtectedRoute><TenantModuleRoute moduleKey="gps_reports"><GPSReportsPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/gps/vehicles/:vehicleId" element={<ProtectedRoute><TenantModuleRoute moduleKey="gps_tracking"><GPSTrackingDetailPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/gps" element={<Navigate to="/gps/live" replace />} />

      {/* Bilty (LR) */}
      <Route path="/bilty" element={<ProtectedRoute><TenantModuleRoute moduleKey="bilty"><BiltyListPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/bilty/items" element={<ProtectedRoute><TenantModuleRoute moduleKey="bilty"><BiltyItemDescriptionsPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/bilty/new" element={<ProtectedRoute><TenantModuleRoute moduleKey="bilty" action="create"><BiltyFormPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/bilty/:id/edit" element={<ProtectedRoute><TenantModuleRoute moduleKey="bilty" action="edit"><BiltyFormPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/bilty/:id" element={<ProtectedRoute><TenantModuleRoute moduleKey="bilty"><BiltyDetailPage /></TenantModuleRoute></ProtectedRoute>} />

      <Route path="/staff" element={<Navigate to="/roles?tab=staff" replace />} />

      <Route path="/trips" element={<ProtectedRoute><TenantModuleRoute moduleKey="trips"><TripsListPage /></TenantModuleRoute></ProtectedRoute>} />

      <Route path="/invoices" element={<ProtectedRoute><TenantModuleRoute moduleKey="invoices"><InvoiceListPage /></TenantModuleRoute></ProtectedRoute>} />
      <Route path="/invoices/:id" element={<ProtectedRoute><TenantModuleRoute moduleKey="invoices"><InvoiceDetailPage /></TenantModuleRoute></ProtectedRoute>} />

      {/* Core TMS modules (dynamic routes) */}
      {tenantModules.filter((m) => m.key !== 'bilty' && m.key !== 'staff' && m.key !== 'trips' && m.key !== 'invoices' && m.key !== 'fleet_reports').map((m) => (
        <Route
          key={m.key}
          path={m.path}
          element={
            <ProtectedRoute>
              <TenantModuleRoute moduleKey={m.key}>
                <ModulePage moduleKey={m.key} />
              </TenantModuleRoute>
            </ProtectedRoute>
          }
        />
      ))}

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LocaleProvider>
        <AppThemeProvider>
          <ToastProvider>
            <ModuleProvider>
              <NotificationProvider>
                <Router basename={APP_BASE_PATH || undefined}>
                  <ErrorBoundary>
                    <AppRoutes />
                  </ErrorBoundary>
                </Router>
              </NotificationProvider>
            </ModuleProvider>
          </ToastProvider>
        </AppThemeProvider>
      </LocaleProvider>
    </AuthProvider>
  );
}

export default App;
